import { EditorState, Modifier, convertToRaw, ContentState } from 'draft-js';
import htmlToDraft from 'html-to-draftjs';
import draftToHtml from 'draftjs-to-html';
import { htmlToText } from './util';

export const DraftHelpers = {
  // Input may be either text or html.
  setContents: (html, editorState) => {
    if (typeof html !== 'string' || !editorState) {
      throw new Error('DraftHelpers.setContents invoked with missing parameters');
    }
    html = DraftHelpers.textToDraftHtml(html);
    const closingPTag = '</p>';
    const htmlEnd = html.length - closingPTag.length >= 0 ? html.substring(html.length - closingPTag.length) : '';
    if (htmlEnd !== closingPTag) {
      // Append an empty paragraph to support adding content after a macro.
      html += '<p></p>';
    }
    html = DraftHelpers.fixDraftFormatting(html);
    const newDraft = htmlToDraft(html);
    const { contentBlocks, entityMap } = newDraft;
    const newContent = ContentState.createFromBlockArray(contentBlocks, entityMap);
    let newEditorState = EditorState.createWithContent(newContent);
    newEditorState = EditorState.push(editorState, newContent, 'insert-fragment');
    newEditorState = EditorState.moveFocusToEnd(newEditorState);

    return newEditorState;
  },

  // Only pure text is accepted here. HTML is not accepted here.
  replaceCurrentSelection: (text, editorState) => {
    if (typeof text !== 'string' || !editorState) {
      throw new Error('DraftHelpers.replaceCurrentSelection invoked with missing parameters');
    }
    const currentContent = editorState.getCurrentContent();
    const currentSelection = editorState.getSelection();
    const newContent = Modifier.replaceText(currentContent, currentSelection, text);
    let newEditorState = EditorState.push(editorState, newContent, 'insert-characters');
    newEditorState = EditorState.forceSelection(newEditorState, newContent.getSelectionAfter());

    return newEditorState;
  },

  // Only pure text is accepted here. HTML is not accepted here.
  replaceRegularExpression: (textToAdd, editorState, regEx, removeSpaces) => {
    if (typeof textToAdd !== 'string' || !editorState) {
      throw new Error('DraftHelpers.appendHtmlToContents invoked with missing parameters');
    }

    const currentContentState = editorState.getCurrentContent();
    const plainText = editorState.getCurrentContent().getPlainText() || '';
    const currentHtml = plainText ? DraftHelpers.contentToHtml(currentContentState) : '';
    let newText = currentHtml;
    if (removeSpaces) {
      // removes line breaks - not needed when inserting text, only removing
      newText = newText.replace(`<br><br>${regEx}`, textToAdd);
    }
    // still needed if no line breaks
    newText = newText.replace(regEx, textToAdd);
    if (newText.length === 0) {
      return EditorState.moveFocusToEnd(editorState);
    }

    return DraftHelpers.setContents(newText, editorState);
  },

  // Input may be either text or html.
  appendHtmlToContents: (html, editorState) => {
    if (typeof html !== 'string' || !editorState) {
      throw new Error('DraftHelpers.appendHtmlToContents invoked with missing parameters');
    }

    const currentContentState = editorState.getCurrentContent();
    const plainText = editorState.getCurrentContent().getPlainText();
    const currentHtml = plainText ? DraftHelpers.contentToHtml(currentContentState) : '';
    if (currentHtml.length === 0 && html.length === 0) {
      return EditorState.moveFocusToEnd(editorState);
    }
    html = DraftHelpers.textToDraftHtml(html);

    return DraftHelpers.setContents(currentHtml + html, editorState);
  },

  contentToHtml: (content) => {
    let html = content ? draftToHtml(convertToRaw(content)) : '';
    html = DraftHelpers.fixDraftFormatting(html);

    return html;
  },

  // Fixes malformed macros: wraps every text with p tags, but keeps img tags outside of them.
  // Designed specifically for fixing decision tree macros that do not have p tags around text but do have img tags.
  // Also, removes externeous vertical spaces created with newline characters.
  textToDraftHtml: (html) => {
    html = html || '';
    html = html.trim();
    if (htmlToText(html).length !== 0 && (!html.match(/^<p>/) || !html.match(/<\/p>$/))) {
      html = html.replace(/<img(\s+.*?)src.+?>/gi, (match) => {
        return '</p>' + match + '<p>';
      });
      if (html.indexOf('<p>') !== 0) {
        html = '<p>' + html + '</p>';
      }
      html = html.replace(/\n\n<\/p>/g, '</p>');
    }

    return html;
  },

  // Draft / Draft conversion functions insert newlines after closing p and img tags,
  // and creates empty p tags with new lines for empty p tags.
  // This function removes any whitespaces/newlines between adjacent p and img elements,
  // and puts an html empty space between empty p tags.
  fixDraftFormatting: (html) => {
    html = html || '';
    html = html.replace(/(<\/p>|<img.*?\/>)(\s+)(<p>|<img.*?\/>)/g, (match, g1, g2, g3) => {
      return g1 + g3;
    });
    html = html.replace(/<p><\/p>/g, '<p>&nbsp;</p>').trim();

    return html;
  },

  marginTop: '1em',
  marginBottom: '1em',
};
